/**
 * ContextMenu Component
 *
 * A generic right-click context menu that can be used throughout the application.
 * Supports keyboard navigation and click-outside-to-close.
 */

import { useEffect, useRef, CSSProperties, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface ContextMenuItem {
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  separator?: boolean;
}

export interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // POSITION ADJUSTMENT (keep menu on screen)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!menuRef.current) return;

    const menu = menuRef.current;
    const rect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Adjust horizontal position if menu goes off screen
    if (rect.right > viewportWidth) {
      menu.style.left = `${viewportWidth - rect.width - 10}px`;
    }

    // Adjust vertical position if menu goes off screen
    if (rect.bottom > viewportHeight) {
      menu.style.top = `${viewportHeight - rect.height - 10}px`;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // CLICK OUTSIDE TO CLOSE
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add slight delay to prevent immediate close from the right-click that opened it
    const timeoutId = setTimeout(() => {
      // Use capture phase to catch events before React Flow
      document.addEventListener('mousedown', handleClickOutside, true);
      // Also add on bubble phase as fallback
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose]);

  // ---------------------------------------------------------------------------
  // KEYBOARD NAVIGATION
  // ---------------------------------------------------------------------------

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
      // TODO: Add arrow key navigation between items
    },
    [onClose]
  );

  // ---------------------------------------------------------------------------
  // ITEM CLICK
  // ---------------------------------------------------------------------------

  const handleItemClick = useCallback(
    (item: ContextMenuItem) => {
      if (!item.disabled) {
        item.onClick();
        onClose();
      }
    },
    [onClose]
  );

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const menuStyles: CSSProperties = {
    position: 'fixed',
    left: x,
    top: y,
    backgroundColor: 'var(--nb-bg-secondary)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    minWidth: 180,
    zIndex: 10000,
    padding: 'var(--nb-space-xs) 0',
  };

  const itemStyles = (item: ContextMenuItem, isHovered: boolean): CSSProperties => ({
    padding: 'var(--nb-space-xs) var(--nb-space-md)',
    fontSize: 'var(--nb-font-size-sm)',
    color: item.disabled ? 'var(--nb-text-muted)' : 'var(--nb-text-primary)',
    backgroundColor: isHovered && !item.disabled ? 'var(--nb-bg-active)' : 'transparent',
    cursor: item.disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--nb-space-sm)',
    transition: 'background-color var(--nb-transition-fast)',
    userSelect: 'none',
  });

  const separatorStyles: CSSProperties = {
    height: 1,
    backgroundColor: 'var(--nb-border-primary)',
    margin: 'var(--nb-space-xs) 0',
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const menu = (
    <div
      ref={menuRef}
      style={menuStyles}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {items.map((item, index) => {
        if (item.separator) {
          return <div key={`separator-${index}`} style={separatorStyles} />;
        }

        return (
          <div
            key={index}
            style={itemStyles(item, false)}
            onMouseEnter={(e) => {
              if (!item.disabled) {
                (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--nb-bg-active)';
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
            }}
            onClick={() => handleItemClick(item)}
          >
            {item.icon && <span>{item.icon}</span>}
            <span>{item.label}</span>
          </div>
        );
      })}
    </div>
  );

  // Render as portal to ensure it's above everything else
  return createPortal(menu, document.body);
}

export default ContextMenu;
